/* ============================================================
   D2 LAYERWORKS — app logic
   - Nav scroll state
   - Product card rendering with real images + fallback placeholder
   - Scroll reveal (IntersectionObserver)
   - Bar-chart fill-on-view
   - 3D hover tilt on product cards
   - Hero parallax on floating shapes
   - Animated counter for stat numbers
   ============================================================ */

(function(){

  /* ---------- NAV scroll state ---------- */
  const nav = document.getElementById('nav');
  const onScroll = () => {
    if(window.scrollY > 8) nav.classList.add('is-scrolled');
    else nav.classList.remove('is-scrolled');
  };
  document.addEventListener('scroll', onScroll, {passive:true});
  onScroll();

  /* ---------- Striped SVG placeholder (fallback only) ---------- */
  function makePlaceholder(palette, label){
    const [c1, c2] = palette;
    const id = 'p' + Math.random().toString(36).slice(2,9);
    return `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300" preserveAspectRatio="xMidYMid slice" role="img" aria-label="${label} placeholder">
        <defs>
          <pattern id="${id}-stripes" patternUnits="userSpaceOnUse" width="14" height="14" patternTransform="rotate(35)">
            <rect width="14" height="14" fill="${c1}" opacity=".08"/>
            <rect width="7" height="14" fill="${c1}" opacity=".14"/>
          </pattern>
          <linearGradient id="${id}-bg" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stop-color="${c1}" stop-opacity=".05"/>
            <stop offset="100%" stop-color="${c2}" stop-opacity=".10"/>
          </linearGradient>
        </defs>
        <rect width="400" height="300" fill="url(#${id}-bg)"/>
        <rect width="400" height="300" fill="url(#${id}-stripes)"/>
        <g font-family="ui-monospace, SFMono-Regular, monospace" font-size="11" fill="${c1}" opacity=".55">
          <text x="20" y="32">D2 // ${label.toUpperCase()}</text>
          <text x="20" y="280">drop product photo here</text>
        </g>
      </svg>
    `;
  }

  /* ---------- Render products ---------- */
  const grid = document.getElementById('productGrid');
  if(grid && window.PRODUCTS){
    const html = window.PRODUCTS.map((p, i) => {
      const visual = p.image
        ? `<img src="${p.image}" alt="${p.name}" loading="lazy" decoding="async" />`
        : makePlaceholder(p.palette, p.name);
      return `
      <article class="product-card ${p.feature ? 'feature' : ''}" style="--d:${(i*0.06).toFixed(2)}s" data-id="${p.id}">
        <div class="pc-img">
          <div class="pc-img-inner">${visual}</div>
          ${p.badge ? `<span class="pc-badge ${p.badge}">${p.badge === 'new' ? 'New' : 'Eco'}</span>` : ''}
        </div>
        <div class="pc-top">
          <h3 class="pc-title">${p.name}</h3>
          <span class="pc-price">${p.price}</span>
        </div>
        <p class="pc-desc">${p.desc}</p>
        <div class="pc-foot">
          <div class="pc-tags">
            ${p.tags.map(t => `<span class="pc-tag">${t}</span>`).join('')}
          </div>
          <span class="pc-cta">Enquire <span class="pc-cta-arrow">→</span></span>
        </div>
      </article>
    `;}).join('');

    const comingSlot = `
      <a href="#contact" class="product-card coming" style="--d:${(window.PRODUCTS.length*0.06).toFixed(2)}s">
        <span class="plus" aria-hidden="true">+</span>
        <p>New products dropping monthly.<br/>Got a request?</p>
      </a>
    `;

    grid.innerHTML = html + comingSlot;
    attachTilt();
  }

  /* ---------- 3D hover tilt on product cards ---------- */
  function attachTilt(){
    if(window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if(window.matchMedia('(hover: none)').matches) return; // skip on touch

    const cards = document.querySelectorAll('.product-card:not(.coming)');
    cards.forEach(card => {
      let rect = null;
      let raf = null;

      const onEnter = () => {
        rect = card.getBoundingClientRect();
        card.style.transition = 'transform .15s ease-out, box-shadow .35s ease, border-color .35s ease';
      };
      const onMove = (e) => {
        if(!rect) return;
        if(raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const x = (e.clientX - rect.left) / rect.width;   // 0..1
          const y = (e.clientY - rect.top) / rect.height;   // 0..1
          const rotY = (x - 0.5) * 8;   // deg
          const rotX = (0.5 - y) * 8;
          card.style.transform = `perspective(900px) rotateX(${rotX}deg) rotateY(${rotY}deg) translateY(-4px)`;
          const img = card.querySelector('.pc-img-inner');
          if(img) img.style.transform = `translateX(${(x - 0.5) * 12}px) translateY(${(y - 0.5) * 8}px) scale(1.06)`;
        });
      };
      const onLeave = () => {
        rect = null;
        card.style.transition = 'transform .6s cubic-bezier(.2,.7,.2,1), box-shadow .35s ease, border-color .35s ease';
        card.style.transform = '';
        const img = card.querySelector('.pc-img-inner');
        if(img) img.style.transform = '';
      };

      card.addEventListener('mouseenter', onEnter);
      card.addEventListener('mousemove', onMove);
      card.addEventListener('mouseleave', onLeave);
    });
  }

  /* ---------- Hero parallax — float + mouse follow ---------- */
  (function heroParallax(){
    if(window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const hero = document.querySelector('.hero');
    if(!hero) return;
    const shapes = hero.querySelectorAll('[data-parallax]');
    if(!shapes.length) return;

    let mx = 0, my = 0;
    let scrollY = window.scrollY;
    let ticking = false;

    function update(){
      shapes.forEach(s => {
        const depth = parseFloat(s.dataset.parallax) || 1;
        const tx = mx * 20 * depth;
        const ty = my * 14 * depth - scrollY * 0.08 * depth;
        s.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
      });
      ticking = false;
    }
    function schedule(){
      if(!ticking){ ticking = true; requestAnimationFrame(update); }
    }

    hero.addEventListener('mousemove', (e) => {
      const rect = hero.getBoundingClientRect();
      mx = (e.clientX - rect.left) / rect.width - 0.5;
      my = (e.clientY - rect.top) / rect.height - 0.5;
      schedule();
    });
    document.addEventListener('scroll', () => {
      scrollY = window.scrollY;
      schedule();
    }, {passive:true});
    update();
  })();

  /* ---------- Animated counter (stats) ---------- */
  function animateCount(el){
    const target = parseFloat(el.dataset.count);
    if(!isFinite(target)) return;
    const suffix = el.dataset.suffix || '';
    const isFloat = String(el.dataset.count).includes('.');
    const dur = 1400;
    const start = performance.now();
    function tick(now){
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      const v = target * eased;
      const num = isFloat ? v.toFixed(1) : Math.round(v);
      el.innerHTML = num + (suffix ? `<span class="unit">${suffix}</span>` : '');
      if(t < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  /* ---------- Scroll reveal ---------- */
  const targets = document.querySelectorAll('.reveal, .product-card');
  if('IntersectionObserver' in window){
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if(e.isIntersecting){
          e.target.classList.add('is-in');
          if(e.target.matches('[data-count]')) animateCount(e.target);
          io.unobserve(e.target);
        }
      });
    }, {threshold: 0.08, rootMargin: '0px 0px -40px 0px'});
    targets.forEach(t => io.observe(t));

    // Also observe stat .num elements directly so they animate even when their wrapper has already revealed
    document.querySelectorAll('.num[data-count]').forEach(t => io.observe(t));
  } else {
    targets.forEach(t => t.classList.add('is-in'));
    document.querySelectorAll('.num[data-count]').forEach(el => { el.textContent = el.dataset.count; });
  }

  /* ---------- Animate sustainability bars on view ---------- */
  const sustainSec = document.getElementById('sustainability');
  if(sustainSec && 'IntersectionObserver' in window){
    const bars = sustainSec.querySelectorAll('.bar-fill');
    bars.forEach(b => {
      b.dataset.target = b.style.width;
      b.style.width = '0';
    });
    const io2 = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if(e.isIntersecting){
          bars.forEach((b, i) => {
            setTimeout(() => { b.style.width = b.dataset.target; }, 200 + i*180);
          });
          io2.disconnect();
        }
      });
    }, {threshold: 0.3});
    io2.observe(sustainSec);
  }

})();
